import { useState, useEffect } from "react";
import { X, Download, Smartphone, Share } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { usePWA } from "@/hooks/usePWA";
import { Link } from "react-router-dom";

/**
 * @deprecated Use SetupProgressCard instead for inline experience.
 * This component is kept for backward compatibility but should be phased out.
 * The floating prompt overlaps content and creates poor UX.
 */
export function InstallPrompt() {
  const { isInstallable, isInstalled, isIOS, isStandalone, install } = usePWA();
  const [showPrompt, setShowPrompt] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if prompt was dismissed recently (within 14 days - increased for less intrusion)
    const dismissedAt = localStorage.getItem("pwa_prompt_dismissed");
    if (dismissedAt) {
      const dismissedDate = new Date(dismissedAt);
      const daysSinceDismissed = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 14) {
        setDismissed(true);
        return;
      }
    }

    // Check if setup card is visible (don't show both)
    const setupCardDismissed = localStorage.getItem("setup_card_dismissed");
    if (!setupCardDismissed) {
      // SetupProgressCard will handle the install prompt
      setDismissed(true);
      return;
    }

    // Show prompt after a longer delay for better UX
    const timer = setTimeout(() => {
      if ((isInstallable || isIOS) && !isInstalled && !isStandalone) {
        setShowPrompt(true);
      }
    }, 15000); // 15 seconds instead of 3

    return () => clearTimeout(timer);
  }, [isInstallable, isInstalled, isIOS, isStandalone]);

  const handleDismiss = () => {
    setShowPrompt(false);
    setDismissed(true);
    localStorage.setItem("pwa_prompt_dismissed", new Date().toISOString());
  };

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      setShowPrompt(false);
    }
  };

  if (!showPrompt || dismissed || isInstalled || isStandalone) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:max-w-sm animate-in slide-in-from-bottom-4 duration-300 safe-area-bottom">
      <Card className="border-primary/20 bg-card shadow-xl">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Smartphone className="h-5 w-5 text-primary" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="font-semibold text-foreground text-sm">
                    Instalar Sentinela
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Acesse rápido direto da sua tela inicial
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Fechar prompt de instalação"
                  className="h-6 w-6 shrink-0 -mt-1 -mr-1"
                  onClick={handleDismiss}
                >
                  <X className="h-4 w-4" aria-hidden="true" />
                </Button>
              </div>

              {isIOS ? (
                <Link to="/instalar" className="block mt-3">
                  <Button size="sm" className="w-full">
                    <Share className="h-4 w-4 mr-2" />
                    Ver instruções
                  </Button>
                </Link>
              ) : (
                <Button
                  size="sm"
                  className="mt-3 w-full"
                  onClick={handleInstall}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Instalar App
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
